import type { MailboxInfo } from "owa-client-types";
import { TasksHubSection } from "../utils/TasksHubSection";
import { getSurfacedAsyncItems, type SurfacedAsyncItem, } from "owa-copilot-async-chat-utils/lib/selectors/getSurfacedAsyncItems";

export type EntriesBySection = Record<TasksHubSection, SurfacedAsyncItem[]>;

function getUpdateTime(item: SurfacedAsyncItem): number {
    // Sort entries with no known update time as the most recent (top of the list).
    return item.entry.updateTimeUtc ?? Number.POSITIVE_INFINITY;
}

/**
 * The Tasks Hub section an item belongs to: `inProgress` items form the In progress section; every
 * other surfaced state (failed / needs-attention / completed) rolls up into Needs your input.
 */
export function getSectionForItem(item: SurfacedAsyncItem): TasksHubSection {
    return item.state === "inProgress"
        ? TasksHubSection.InProgress
        : TasksHubSection.NeedsYourInput;
}

/**
 * Returns all async items for an account grouped into the Tasks Hub sections, each sorted most-recent
 * first.
 */
export default function getEntriesBySection(mailboxInfo: MailboxInfo): EntriesBySection {
    const sections: EntriesBySection = {
        [TasksHubSection.NeedsYourInput]: [],
        [TasksHubSection.InProgress]: [],
    };
    
    for (const item of getSurfacedAsyncItems(mailboxInfo)) {
        sections[getSectionForItem(item)].push(item);
    }
    
    const byNewest = (a: SurfacedAsyncItem, b: SurfacedAsyncItem): number => {
        const timeA = getUpdateTime(a);
        const timeB = getUpdateTime(b);
        return timeA === timeB ? 0 : timeB - timeA;
    };
    sections[TasksHubSection.NeedsYourInput].sort(byNewest);
    sections[TasksHubSection.InProgress].sort(byNewest);
    
    return sections;
}
